/**
 * ShowcaseConfig — единая точка для размеров/лимитов витрины.
 * Позже список размеров и топы можно подменить с API без переписывания UI.
 */

import { DEMO_STORE_ID } from '../../app/demoWorkspace';

/** @typedef {{ label: string, width: number, profile: number, diameter: string }} TireSizeChip */
/** @typedef {{ label: string, diameter: string, pn: number, pcd: number, cb: number, cbFrom?: number, cbTo?: number }} DiscSizeChip */

export const SHOWCASE_CONFIG = {
  /**
   * Полки карточек («Сейчас в сезоне» / «Литые диски в наличии») только из этого поставщика.
   * Совпадает с `item.supplier` в IDB (label Шинсервиса).
   */
  showcaseSupplier: 'Шинсервис',
  /** То же для frozen demo snapshot с анонимизированными именами. */
  showcaseSupplierDemo: 'ТайрСервис',
  tires: {
    /** @type {TireSizeChip[]} */
    popularSizes: [
      { label: '175/70R13', width: 175, profile: 70, diameter: 'R13' },
      { label: '175/65R14', width: 175, profile: 65, diameter: 'R14' },
      { label: '185/60R14', width: 185, profile: 60, diameter: 'R14' },
      { label: '185/65R15', width: 185, profile: 65, diameter: 'R15' },
      { label: '195/65R15', width: 195, profile: 65, diameter: 'R15' },
      { label: '185/75R16C', width: 185, profile: 75, diameter: 'R16C' },
      { label: '205/55R16', width: 205, profile: 55, diameter: 'R16' },
      { label: '215/65R16', width: 215, profile: 65, diameter: 'R16' },
      { label: '205/55R17', width: 205, profile: 55, diameter: 'R17' },
      { label: '215/55R17', width: 215, profile: 55, diameter: 'R17' },
      { label: '225/65R17', width: 225, profile: 65, diameter: 'R17' },
      { label: '235/45R18', width: 235, profile: 45, diameter: 'R18' },
      { label: '225/60R18', width: 225, profile: 60, diameter: 'R18' },
      { label: '225/55R19', width: 225, profile: 55, diameter: 'R19' },
      { label: '235/55R19', width: 235, profile: 55, diameter: 'R19' },
    ],
    /** Цель полки «Сейчас в сезоне»: ровно до 25 карточек (clamp по наличию). */
    seasonHitsCount: { min: 25, max: 25 },
    /**
     * Whitelist Ikon для партнёрского блока полки «Сейчас в сезоне».
     * SUV/SUF/SAV, Eco C2/C3 и модели, которые сами Nordman, отсекаются.
     * Старое имя в скобках («Character Eco (Nordman SX3)») — это Character, не Nordman.
     */
    ikonSeasonModelsSummer: [
      'Character Eco',
      'Autograph Eco 3',
      'Autograph Aqua 3',
      'Character Aqua',
      'Character Ultra',
      'Autograph Ultra 2',
    ],
    /** Зимние: Character Ice/Snow + Autograph Ice/Snow (Nordman не используем). */
    ikonSeasonModelsWinter: [
      'Character Ice 8',
      'Character Ice 7',
      'Character Ice 5',
      'Character Snow 2',
      'Autograph Ice 9',
      'Autograph Ice 10',
      'Autograph Snow 3',
      'Autograph Snow 5',
    ],
    /**
     * Кандидаты из IDB. Ikon (preferItem) забираются целиком,
     * остальные — до этого лимита суммарно с Ikon.
     */
    candidateLimit: 480,
    /** Целый комплект: на полку только позиции с amount ≥ 4. */
    minAmount: 4,
  },
  discs: {
    /** @type {DiscSizeChip[]} */
    popularSizes: [
      { label: 'R14 PCD 4x98 ЦО 58.6', diameter: 'R14', pn: 4, pcd: 98, cb: 58.6 },
      { label: 'R15 PCD 4x100 ЦО 54.1', diameter: 'R15', pn: 4, pcd: 100, cb: 54.1 },
      { label: 'R15 PCD 4x100 ЦО 60.1', diameter: 'R15', pn: 4, pcd: 100, cb: 60.1 },
      { label: 'R15 PCD 5x100 ЦО 57.1', diameter: 'R15', pn: 5, pcd: 100, cb: 57.1 },
      { label: 'R16 PCD 4x100 ЦО 60.1', diameter: 'R16', pn: 4, pcd: 100, cb: 60.1 },
      { label: 'R16 PCD 5x114.3 ЦО 60.1', diameter: 'R16', pn: 5, pcd: 114.3, cb: 60.1 },
      { label: 'R16 PCD 5x112 ЦО 57.1', diameter: 'R16', pn: 5, pcd: 112, cb: 57.1 },
      { label: 'R16 PCD 5x139.7 ЦО 98', diameter: 'R16', pn: 5, pcd: 139.7, cb: 98, cbFrom: 98, cbTo: 98.6 },
      { label: 'R17 PCD 5x114.3 ЦО 67.1', diameter: 'R17', pn: 5, pcd: 114.3, cb: 67.1 },
      { label: 'R17 PCD 5x105 ЦО 60.1', diameter: 'R17', pn: 5, pcd: 105, cb: 60.1 },
      { label: 'R17 PCD 5x112 ЦО 57.1', diameter: 'R17', pn: 5, pcd: 112, cb: 57.1 },
      { label: 'R18 PCD 5x114.3 ЦО 67.1', diameter: 'R18', pn: 5, pcd: 114.3, cb: 67.1 },
    ],
    popularModelsCount: { min: 15, max: 15 },
    /**
     * Кандидаты — все matching из RAM (candidateLimit: null).
     * На полку нужны все литые Шинсервиса с amount >= minAmount;
     * лимит 15 — уже в buildDiscShowcase (seeded shuffle).
     */
    minAmount: 4,
    /** Тип диска на полке «Литые диски в наличии». */
    diskType: 'Литой',
  },
  /**
   * Soft retail-score (полка шин). Hard filter minAmount (tires/discs) ≠ soft scoring.amountHigh.
   */
  scoring: {
    amountHigh: 4,
    amountLow: 1,
    weights: {
      amountHigh: 40,
      amountLow: 22,
      photo: 14,
      brand: 6,
      hasPrice: 18,
      priceInBand: 8,
    },
    priceBand: { min: 2500, max: 25000 },
  },
  diversity: {
    maxPerBrand: 2,
  },
  /** JS 0-based months: мар–авг = лето (s), иначе зима (w). */
  season: { summerMonthFrom: 2, summerMonthTo: 7 },
  copy: {
    seasonHits: 'Сейчас в сезоне',
    popularModels: 'Литые диски в наличии',
    popularSizes: 'Частые размеры',
    trySizes: 'Другие размеры',
  },
};

/** Поставщик полок витрины: live store vs demo workspace. */
export const getShowcaseSupplier = (storeId) =>
  storeId === DEMO_STORE_ID
    ? SHOWCASE_CONFIG.showcaseSupplierDemo
    : SHOWCASE_CONFIG.showcaseSupplier;

/** Лето: мар–авг; зима: сен–фев. Границы — SHOWCASE_CONFIG.season. */
export const getCatalogSeasonFromDate = (date = new Date()) => {
  const month = date.getMonth();
  const { summerMonthFrom, summerMonthTo } = SHOWCASE_CONFIG.season;
  return month >= summerMonthFrom && month <= summerMonthTo ? 's' : 'w';
};

/**
 * Точное совпадение width/profile/diameter с чипом частых размеров.
 * R16 и R16C — разные размеры; без нормализации «примерно R16».
 */
export const isPopularTireSize = (
  item,
  sizes = SHOWCASE_CONFIG.tires.popularSizes
) => {
  if (!item || !Array.isArray(sizes)) return false;
  const width = Number(item.width);
  const profile = Number(item.profile);
  if (!Number.isFinite(width) || !Number.isFinite(profile)) return false;
  const diameter = String(item.diameter ?? '');
  if (!diameter) return false;
  return sizes.some(
    (size) =>
      Number(size.width) === width &&
      Number(size.profile) === profile &&
      String(size.diameter) === diameter
  );
};
